import {Link as RouterLink} from 'react-router-dom';
import {Link} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {useGetReferrals} from '@entities/referral';
import {User} from '@entities/user';
import {MrTable} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {PaginationState} from '@/shared/types';

const columns: MRT_ColumnDef<User>[] = [
  {
    accessorKey: 'username',
    header: 'User',
    Cell: ({row}) => (
      <Link
        component={RouterLink}
        to={ROUTE__USER_ACCOUNT.replace(':id', row.original.id)}
      >
        {row.original.username}
      </Link>
    ),
  },
  {accessorKey: 'name', header: 'Name'},
  {
    accessorKey: 'registeredAt',
    header: 'Registered',
    Cell: ({row}) =>
      row.original.registeredAt
        ? formatInTz(row.original.registeredAt, DATETIME_FORMAT)
        : '—',
  },
  {
    accessorKey: 'country',
    header: 'Country',
    Cell: ({row}) => row.original.country || '—',
  },
  {
    accessorKey: 'isVerified',
    header: 'Verified',
    Cell: ({row}) => (row.original.isVerified ? 'Yes' : 'No'),
  },
  {
    id: 'referrer',
    header: 'Referrer',
    Cell: ({row}) =>
      row.original.referrer ? (
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', row.original.referrer.id)}
        >
          {row.original.referrer.username}
        </Link>
      ) : (
        '—'
      ),
  },
];

const userReferralColumns = columns.filter(column => column.id !== 'referrer');

interface Props {
  userId?: string;
}

export const ReferralsTable = ({userId}: Props) => {
  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {data, isPending, error} = useGetReferrals({
    userId,
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  return (
    <MrTable
      columns={userId ? userReferralColumns : columns}
      data={data?.items ?? []}
      error={error}
      enableColumnOrdering
      enableColumnDragging={false}
      localStorageKeyForSettings={
        userId ? 'userReferralsTableV5' : 'allReferralsTableV4'
      }
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{isLoading: isPending, pagination: urlState}}
      {...entityTableCommonProps}
      enableRowActions={false}
      enableSorting={false}
      enableColumnFilters={false}
      enableGlobalFilter={false}
    />
  );
};
